import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Film, Plus, ArrowRight, Store, Clock, Flag } from "lucide-react";
import { api } from "../lib/api";

function timeLeft(endDate) {
  const ms = new Date(endDate).getTime() - Date.now();
  if (ms <= 0) return "ended";
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  if (d > 0) return `${d}d ${h}h left`;
  const m = Math.floor((ms % 3600000) / 60000);
  return `${h}h ${m}m left`;
}

export default function Reels() {
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.listReels().then(setReels).finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-8" data-testid="reels-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="lp-section-label">Video competitions</div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mt-2 flex items-center gap-3">
            Reels
            <span className="lp-badge lp-badge-preorder">New</span>
          </h1>
          <p className="text-sm text-gray-600 mt-2 max-w-xl">
            TikTok / Stories-style vertical video competitions. Customers swipe through products and tap to vote.
            Top voted videos win pre-order.
          </p>
        </div>
        <Link to="/app/reels/new" className="lp-btn-primary" data-testid="create-reel-btn">
          <Plus size={16} />
          New reel
        </Link>
      </div>

      <div className="lp-card overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium tracking-tight">All reels</h2>
            <p className="text-xs text-gray-500 mt-1">Each reel needs 2+ products with video.</p>
          </div>
          <span className="text-xs text-gray-500">{reels.length} total</span>
        </div>

        {loading ? (
          <div className="p-12 text-center text-gray-500 text-sm">Loading reels…</div>
        ) : reels.length === 0 ? (
          <div className="p-16 text-center" data-testid="reels-empty-state">
            <div className="w-14 h-14 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center mx-auto mb-4">
              <Film size={22} className="text-gray-400" />
            </div>
            <h3 className="text-lg font-medium tracking-tight">No reels yet</h3>
            <p className="text-sm text-gray-500 mt-2 max-w-sm mx-auto">
              Drop 2+ products with vertical videos into a reel. Best with 9:16 portrait videos under 60 seconds.
            </p>
            <button
              onClick={() => navigate("/app/reels/new")}
              className="lp-btn-primary mt-6"
              data-testid="empty-create-reel-btn"
            >
              <Plus size={16} />
              Create your first reel
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="lp-table" data-testid="reels-table">
              <thead>
                <tr>
                  <th>Reel</th>
                  <th>Status</th>
                  <th>Winners</th>
                  <th>Ends</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {reels.map((r) => (
                  <tr key={r.id} data-testid={`reel-row-${r.id}`}>
                    <td>
                      <div className="font-medium text-gray-900">{r.name}</div>
                      {r.description && <div className="text-xs text-gray-500 mt-0.5 line-clamp-1">{r.description}</div>}
                    </td>
                    <td>
                      {r.status === "active" ? (
                        <span className="lp-badge lp-badge-voting">Active</span>
                      ) : (
                        <span className="lp-badge lp-badge-closed">Completed</span>
                      )}
                    </td>
                    <td className="text-sm font-medium">Top {r.winnersCount}</td>
                    <td>
                      <div className="flex items-center gap-1.5 text-sm text-gray-700">
                        <Clock size={13} className="text-gray-400" />
                        <span>{r.status === "active" ? timeLeft(r.endDate) : "—"}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">{new Date(r.endDate).toLocaleString()}</div>
                    </td>
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/storefront/reels/${r.id}`}
                          className="lp-btn-secondary text-xs"
                          data-testid={`reel-storefront-${r.id}`}
                        >
                          <Store size={14} /> Storefront
                        </Link>
                        <Link
                          to={`/app/reels/${r.id}`}
                          className="lp-btn-primary text-xs"
                          data-testid={`reel-manage-${r.id}`}
                        >
                          {r.status === "active" ? (<>Manage <ArrowRight size={14} /></>) : (<><Flag size={14} /> Review</>)}
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
